/**
 * RecentActivityPill — small coloured pill on each competition card
 * showing the most recent meaningful activity (Feb 2026).
 *
 * Data is fetched in a batched request by the parent list component
 * (see ``useRecentActivity`` hook); this component just renders the
 * result. Colour + icon vary by activity type.
 */
import { Sparkles, Mail, Lock, Clock } from 'lucide-react';

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.floor((now - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

const VARIANTS = {
  draw_applied: {
    bg: 'bg-emerald-50', fg: 'text-emerald-800', dot: 'bg-emerald-500',
    Icon: Sparkles,
  },
  email_sent: {
    bg: 'bg-amber-50', fg: 'text-amber-900', dot: 'bg-amber-500',
    Icon: Mail,
  },
  programme_sent: {
    bg: 'bg-pink-50', fg: 'text-pink-900', dot: 'bg-pink-500',
    Icon: Lock,
  },
  created: {
    bg: 'bg-stone-100', fg: 'text-stone-600', dot: 'bg-stone-400',
    Icon: Clock,
  },
};

export default function RecentActivityPill({ activity, competitionId }) {
  if (!activity || !activity.type || !activity.label) return null;
  const variant = VARIANTS[activity.type] || VARIANTS.created;
  const { Icon } = variant;
  const relative = formatRelative(activity.timestamp);
  const separator = activity.type === 'created' ? ' · Created ' : ' · ';

  return (
    <div className="mb-3" data-testid={`activity-pill-${competitionId}`}>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${variant.bg} ${variant.fg}`}
      >
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">
          {activity.label}
          {relative ? `${separator}${relative}` : ''}
        </span>
      </span>
    </div>
  );
}
